import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ICON = {
  follow: "🫂", favorite: "⭐", like: "❤️", comment: "💬", download: "⬇️",
  friend_request: "🤝", friend_accept: "✅", collab_invite: "🧑‍🤝‍🧑",
  message: "💌", sale: "💰", publish: "🚀", ai_complete: "✨", reward: "🏆",
  guild_pro: "🛡️", remix: "🔀", takedown: "🛡️", models_ready: "🎨",
  vibe_reply: "💬", taunt: "🔥",
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const seenRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 12 } });
      const incoming = data.notifications || [];
      if (seenRef.current === null) {
        seenRef.current = new Set(incoming.map((n) => n.id));
      } else {
        for (const n of incoming) {
          if (!seenRef.current.has(n.id) && !n.read) {
            toast(`${ICON[n.type] || "🔔"} ${n.title}`, { description: n.body });
          }
          seenRef.current.add(n.id);
        }
      }
      setItems(incoming);
      setUnread(data.unread);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadList();
    const t = setInterval(loadList, 30000);
    return () => clearInterval(t);
  }, [loadList]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openPanel = () => { setOpen((v) => !v); if (!open) loadList(); };

  const markAll = async () => {
    await api.post("/notifications/read", {});
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const click = async (n) => {
    if (!n.read) { api.post("/notifications/read", { ids: [n.id] }).catch(() => {}); setUnread((u) => Math.max(0, u - 1)); }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={openPanel} data-testid="notif-bell" className="relative w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))] transition-colors">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-2xl z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
            <span className="font-bold">Notifications</span>
            {unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-[hsl(var(--primary))] font-semibold flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">You're all caught up! 🎉</div>
            ) : items.map((n) => (
              <button key={n.id} onClick={() => click(n)} data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-[hsl(var(--secondary))] transition-colors border-b border-[hsl(var(--border))]/50 ${!n.read ? "bg-[hsl(var(--primary))]/5" : ""}`}>
                <span className="text-xl shrink-0">{n.actor?.avatar || ICON[n.type] || "🔔"}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm leading-snug">{n.title}</div>
                  {n.body && <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{n.body}</div>}
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[hsl(var(--primary))] shrink-0 mt-1.5" />}
              </button>
            ))}
          </div>
          <button onClick={() => { setOpen(false); navigate("/app/notifications"); }} data-testid="notif-see-all" className="w-full py-2.5 text-sm font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))] transition-colors">
            See all notifications
          </button>
        </div>
      )}
    </div>
  );
}
